import { useCallback, useMemo, useState } from 'react';
import { presetFor } from '@app/shared';
import { isApiFailure } from '../../api/http';
import type { MyAppointmentDto } from '../../api/types';
import { Stars } from '../../ui/rating';
import { useAppApi } from '../../app/ApiProvider';
import {
  canCancel,
  canConfirm,
  countNoShows,
  splitAppointments,
  statusTagKey,
  statusTone,
} from '../../domain/appointments';
import { cancelWindowFor, type CancelWindow } from '../../domain/cancel';
import { formatDateTimeInTz, formatTimeInTz } from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaButton, SmallButton } from '../../ui/Cta';
import { IndustryTile, LangSwitch, Tag } from '../../ui/controls';
import { Alert, EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, SectionTitle, TopBar } from '../../ui/Screen';
import { RestrictionSheet } from './RestrictionSheet';

/**
 * «Мои записи» (ТЗ §14.1) плюс два требования антиспама (ТЗ §14.2):
 *   • у каждой предстоящей записи видно, до какого момента отмена безопасна, а после
 *     этого момента кнопка меняет текст на «Отменить (поздняя отмена)»;
 *   • после первой неявки сверху висит баннер-предупреждение.
 *
 * Чьи это записи — определяет подпись `initData`, параметра клиента в запросе нет и быть
 * не может (см. `listMyAppointments` в API-клиенте).
 */
export function MyAppointmentsScreen(props: {
  onOpenBusiness: (slug: string) => void;
}): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();

  const { state, reload } = useAsync(
    useCallback(() => api.client.listMyAppointments(), [api]),
    [],
  );

  const [pendingCancel, setPendingCancel] = useState<{
    item: MyAppointmentDto;
    window: CancelWindow;
  } | null>(null);
  const [cancelResult, setCancelResult] = useState<{ isLateCancel: boolean } | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const items = state.status === 'ready' ? state.data : [];
  const { upcoming, past } = useMemo(() => splitAppointments(items), [items]);
  const noShows = useMemo(() => countNoShows(items), [items]);

  const confirm = async (item: MyAppointmentDto): Promise<void> => {
    setBusyId(item.id);
    setActionError(null);
    try {
      await api.client.confirmAppointment(item.id);
      reload();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusyId(null);
    }
  };

  const rate = async (item: MyAppointmentDto, stars: number): Promise<void> => {
    setBusyId(item.id);
    setActionError(null);
    try {
      await api.client.rateAppointment(item.id, stars);
      reload();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (item: MyAppointmentDto): Promise<void> => {
    setBusyId(item.id);
    setActionError(null);
    try {
      const result = await api.client.cancelAppointment(item.id);
      setPendingCancel(null);
      // `isLateCancel` считает сервер и он же его отдаёт: показываем ЕГО ответ, а не свою
      // оценку. Разойдись они — клиент увидел бы «отмена безопасная» там, где организации
      // ушла пометка о поздней.
      setCancelResult({ isLateCancel: result.isLateCancel });
      reload();
    } catch (error) {
      setPendingCancel(null);
      setActionError(error);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Screen>
      <TopBar title={t('myBookTitle')} subtitle={t('myBookSub')} right={<LangSwitch />} />

      <ScrollArea bottomGap="tabs">
        {state.status === 'loading' ? <Loader /> : null}
        {state.status === 'failed' ? <ErrorState error={state.error} onRetry={reload} /> : null}

        {state.status === 'ready' ? (
          <div className="px-4 pt-3.5">
            <NoShowBanner count={noShows} onOpenRules={() => setRulesOpen(true)} />

            {items.length === 0 ? <EmptyState>{t('myBookEmpty')}</EmptyState> : null}

            {items.length > 0 ? (
              <>
                <SectionTitle className="px-0.5">{t('upcoming')}</SectionTitle>
                {upcoming.length === 0 ? (
                  <EmptyState>{t('upcomingEmpty')}</EmptyState>
                ) : (
                  upcoming.map((item) => (
                    <AppointmentCard
                      key={item.id}
                      item={item}
                      busy={busyId === item.id}
                      onConfirm={() => void confirm(item)}
                      onCancel={(window) => setPendingCancel({ item, window })}
                      onRepeat={() => props.onOpenBusiness(item.business.slug)}
                    />
                  ))
                )}

                <div className="my-3 rounded-[11px] bg-surface-2 px-3 py-2.5 text-[12px] text-ink-2">
                  {t('reminderNote')}
                </div>

                <SectionTitle className="px-0.5">{t('past')}</SectionTitle>
                {past.length === 0 ? (
                  <EmptyState>{t('pastEmpty')}</EmptyState>
                ) : (
                  past.map((item) => (
                    <AppointmentCard
                      key={item.id}
                      item={item}
                      busy={busyId === item.id}
                      onConfirm={() => void confirm(item)}
                      onCancel={(window) => setPendingCancel({ item, window })}
                      onRepeat={() => props.onOpenBusiness(item.business.slug)}
                      onRate={(stars) => void rate(item, stars)}
                    />
                  ))
                )}
              </>
            ) : null}
          </div>
        ) : null}
      </ScrollArea>

      {/* Подтверждение отмены. Текст зависит от того, закрылось ли безопасное окно. */}
      <BottomSheet
        open={pendingCancel !== null}
        title={t('cancelSheetT')}
        onClose={() => setPendingCancel(null)}
      >
        <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">
          {pendingCancel?.window.isLate ? t('cancelSheetLate') : t('cancelSheetSafe')}
        </div>
        {pendingCancel ? (
          <div className="flex gap-2">
            <CtaButton variant="ghost" onClick={() => setPendingCancel(null)}>
              {t('cancelKeep')}
            </CtaButton>
            <CtaButton
              variant="danger"
              disabled={busyId === pendingCancel.item.id}
              onClick={() => void cancel(pendingCancel.item)}
            >
              {pendingCancel.window.isLate ? t('cancelLate') : t('cancelBooking')}
            </CtaButton>
          </div>
        ) : null}
      </BottomSheet>

      <BottomSheet
        open={cancelResult !== null}
        title={cancelResult?.isLateCancel ? t('cancelledLateT') : t('cancelledT')}
        onClose={() => setCancelResult(null)}
      >
        <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">
          {cancelResult?.isLateCancel ? t('cancelledLateB') : t('cancelledB')}
        </div>
        <CtaButton variant="ghost" onClick={() => setCancelResult(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      <BottomSheet
        open={actionError !== null}
        title={t('errTitle')}
        onClose={() => setActionError(null)}
      >
        <ErrorState error={actionError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setActionError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      {/* Та же шторка, что показывает бан, но без конкретного ограничения: из баннера
          о неявке клиент попадает на объяснение правил, а не на сообщение о запрете. */}
      <RestrictionSheet open={rulesOpen} restriction={null} onClose={() => setRulesOpen(false)} />
    </Screen>
  );
}

/**
 * Баннер после неявки (ТЗ §14.2).
 *
 * Счётчик берётся из статусов видимой истории (см. `countNoShows`): отдельного поля
 * «сколько у меня неявок» в клиентском API нет. Поэтому баннер — предупреждение, а не
 * приговор: шкалу санкций считает бэкенд по `no_show_events` (AGENTS.md, правило 6).
 */
function NoShowBanner(props: { count: number; onOpenRules: () => void }): JSX.Element | null {
  const { t } = useI18n();
  if (props.count <= 0) return null;

  const many = props.count > 1;
  return (
    <Alert
      tone={many ? 'danger' : 'warn'}
      title={many ? t('nsWarnManyT') : t('nsWarnT')}
      action={{ label: t('nsWarnLink'), onClick: props.onOpenRules }}
    >
      {many ? t('nsWarnManyB', { n: props.count }) : t('nsWarnB')}
    </Alert>
  );
}

function AppointmentCard(props: {
  item: MyAppointmentDto;
  busy: boolean;
  onConfirm: () => void;
  onCancel: (window: CancelWindow) => void;
  onRepeat: () => void;
  onRate?: (stars: number) => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const { item } = props;
  const preset = presetFor(item.business.type);
  const tz = item.business.tz;

  /**
   * Граница безопасной отмены. Окно берётся из строки организации (`cancelWindowHours`),
   * а не из пресета отрасли: пресет после создания организации не читается (AGENTS.md,
   * правило 5), и владелец мог его изменить.
   *
   * `Date()` берётся на отрисовке: точность до минуты здесь не нужна, а таймер, тикающий
   * ради смены подписи кнопки, держал бы экран в постоянной перерисовке. Финальное слово
   * всё равно за сервером — он возвращает `isLateCancel` в ответе на отмену.
   */
  const window: CancelWindow = cancelWindowFor(
    new Date(item.startsAt),
    item.business.cancelWindowHours,
    new Date(),
  );

  const tagKey = statusTagKey(item.status);

  return (
    <div className="mb-2.5 flex gap-3 rounded-card border-l-[3px] border-l-accent bg-surface px-3.5 py-3.5 shadow-[inset_0_0_0_1px_var(--hair)]">
      <IndustryTile type={preset.type} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            <div className="text-[14px] font-bold">
              {lang === 'ru' ? item.service.titleRu : item.service.titleUz}
            </div>
            <div className="mt-0.5 truncate text-[12.5px] text-ink-3">
              {item.staff.name} · {item.business.name}
            </div>
            <div className="mt-1.5 text-[12.5px] font-bold text-ink-2">
              {formatDateTimeInTz(item.startsAt, tz, lang)}
            </div>
          </div>
          <Tag tone={statusTone(item.status)}>{t(tagKey)}</Tag>
        </div>

        {canCancel(item) ? (
          <div
            className={`mt-1.5 text-[11.5px] font-semibold leading-[1.35] ${
              window.isLate ? 'text-danger' : 'text-green'
            }`}
          >
            {window.isLate
              ? t('lateCancelNote', { time: formatTimeInTz(window.deadline, tz, lang) })
              : t('safeCancelNote', { time: formatTimeInTz(window.deadline, tz, lang) })}
          </div>
        ) : null}

        {/* Оценка — только состоявшегося визита; сервер проверяет то же самое. */}
        {item.status === 'COMPLETED' && props.onRate ? (
          <div className="mt-2 flex items-center gap-2 text-[12px] font-semibold text-ink-2">
            <span>{item.myStars !== null ? t('rateDone') : t('rateAsk')}</span>
            {item.myStars !== null ? (
              <Stars value={item.myStars} size={15} />
            ) : props.busy ? null : (
              <Stars value={0} size={20} onPick={props.onRate} />
            )}
          </div>
        ) : null}

        <div className="mt-2.5 flex gap-2">
          {canConfirm(item) ? (
            <SmallButton variant="ok" disabled={props.busy} onClick={props.onConfirm}>
              {t('confirmBooking')}
            </SmallButton>
          ) : null}
          {canCancel(item) ? (
            <SmallButton
              variant={window?.isLate ? 'warn' : 'ghost'}
              disabled={props.busy}
              onClick={() => props.onCancel(window)}
            >
              {window?.isLate ? t('cancelLate') : t('cancelBooking')}
            </SmallButton>
          ) : null}
          {item.isPast ? (
            <SmallButton variant="accent" onClick={props.onRepeat}>
              {t('bookAgain')}
            </SmallButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}
